import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Ban,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LandPlot,
  Loader2,
  Plus,
  RefreshCw,
  Square,
  Trash2,
  X,
} from 'lucide-react';

import {
  pasHatchSessionService,
  type PasHatchJobSnapshot,
} from '../../services/pasHatchSessionService';
import { requestPasCropPreview, requestPasRegionVision } from '../../services/pasRegionVisionService';
import { assessPasHatchComplexity } from '../../utils/pasHatchComplexity';
import {
  countActivePasPracticeItems,
  mergePasPracticeLibraryItems,
  refreshPasPracticeLibrary,
  togglePasPracticeNegativeExample,
  type PasPracticeLibraryItem,
} from '../../utils/pasPracticeLibrary';
import { resolveSubjectCode } from '../../utils/subjectConfig';
import { isPasAnchorPracticeSequenceId, PAS_ANCHOR_PRACTICE_SEQUENCE_ID } from '../../utils/pasAsset';
import { PasDrsPanel } from './PasDrsPanel';

const TARGET_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const DEFAULT_TARGET_COUNT = 5;

/** One ladder practice sequence in the library (group 0 = anchor entrances, 1+ = canvas crops). */
export type PasPracticeRegionSequence = {
  id: string;
  groupIndex?: number;
  label: string;
  practiceItems: PasPracticeLibraryItem[];
};

function isEnglishPasSubject(value: unknown): boolean {
  return resolveSubjectCode(value) === 'english';
}

function resolvePasHatchPlaceholder(subject: unknown): string {
  const code = resolveSubjectCode(subject) ?? 'math';
  if (code === 'english') {
    return '例如：框选词表缺中文释义，按「英译中挖空」补全后出变式…';
  }
  if (code === 'yuwen') {
    return '例如：框选缺拼音，按「形近字听写」补全后出变式…';
  }
  return '例如：框选里缺了被减数，按「序数个数」补全后出变式…';
}

/** Legacy upstream used to dump these as hard failures — treat as soft warning chrome only. */
function isHeuristicPrinciplesFailMessage(message: string): boolean {
  const m = String(message || '').trim();
  if (!m) return false;
  return /^(快：偏向长演算|锋利：目标过宽|闭目可想：依赖复杂看图|短：孵化词偏长)/.test(m)
    || /已废弃的四原则/.test(m);
}

function LibraryDifficultyBadge({ level }: { level: number }) {
  const clamped = Math.max(1, Math.min(5, Math.round(level)));
  const caption = clamped >= 5
    ? '贴近原题'
    : clamped >= 4
      ? '很接近'
      : clamped >= 3
        ? '较近'
        : clamped >= 2
          ? '过渡'
          : '远离原题';
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-muted"
      title={`难度 ${clamped}/5 · ${caption}`}
    >
      <span className="font-medium text-ink/70">难度</span>
      <span className="flex gap-0.5" aria-hidden>
        {[1, 2, 3, 4, 5].map((slot) => (
          <span
            key={slot}
            className={
              'h-1.5 rounded-full '
              + (slot <= clamped ? 'w-2.5 bg-primary-strong' : 'w-1.5 bg-border')
            }
          />
        ))}
      </span>
      <span className="text-muted">{caption}</span>
    </span>
  );
}

function sortPracticeItemsByProximity(items: PasPracticeLibraryItem[]): PasPracticeLibraryItem[] {
  return [...items].sort((a, b) => {
    const pa = typeof a.proximityLevel === 'number' ? a.proximityLevel : -1;
    const pb = typeof b.proximityLevel === 'number' ? b.proximityLevel : -1;
    if (pb !== pa) return pb - pa;
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''));
  });
}

type PasPracticeDrawerProps = {
  open: boolean;
  regionId: string | null;
  assetId: string;
  sourceKpId?: string | null;
  /** Asset subject — routes hatch to pas-yuwen/english/math-pkp. */
  subject?: string | null;
  initialHatchPrompt: string;
  initialPracticeItems?: PasPracticeLibraryItem[];
  /** All canvas crops with their ladder sequences (math DRS multi-crop). */
  regionSequences?: PasPracticeRegionSequence[];
  onSelectRegionSequence?: (regionId: string) => void;
  cropRectNormalized?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /** Cached crop vision from admit. */
  regionContent?: string;
  regionFeedback?: string;
  onClose: () => void;
  onHatchPromptChange?: (regionId: string, hatchPrompt: string) => void;
  onPracticeLibraryChange?: (sequenceId: string, items: PasPracticeLibraryItem[]) => void | Promise<void>;
  onRegionVisionChange?: (
    regionId: string,
    vision: { regionContent: string; regionFeedback: string },
  ) => void;
  onToast?: (message: string) => void;
  autoEnumerateDrs?: boolean;
  /** `page`：占满工作台区域；`drawer`：右侧滑出遮罩抽屉（保留兼容） */
  presentation?: 'drawer' | 'page';
  /** Side-by-side layout: no back-button gutter on the left */
  embedded?: boolean;
};

export function PasPracticeDrawer({
  open,
  regionId,
  assetId,
  sourceKpId,
  subject,
  initialHatchPrompt,
  initialPracticeItems,
  regionSequences,
  onSelectRegionSequence,
  cropRectNormalized,
  regionContent,
  regionFeedback,
  onClose,
  onHatchPromptChange,
  onPracticeLibraryChange,
  onRegionVisionChange,
  onToast,
  autoEnumerateDrs = false,
  presentation = 'page',
  embedded = false,
}: PasPracticeDrawerProps) {
  const [hatchPrompt, setHatchPrompt] = useState(initialHatchPrompt);
  const [savedHatchPrompt, setSavedHatchPrompt] = useState(initialHatchPrompt);
  const [targetCount, setTargetCount] = useState<number>(DEFAULT_TARGET_COUNT);
  const [items, setItems] = useState<PasPracticeLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
    produced?: number;
    failed?: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offerProRetry, setOfferProRetry] = useState(false);
  const [usingProModel, setUsingProModel] = useState(false);
  /** Latest model feedback (vision gap / incomplete conditions / hatch complaint). */
  const [modelFeedback, setModelFeedback] = useState<string | null>(null);
  const [localRegionContent, setLocalRegionContent] = useState(regionContent || '');
  const [visionLoading, setVisionLoading] = useState(false);
  const [cropPreviewUrl, setCropPreviewUrl] = useState<string | null>(null);
  const [cropPreviewLoading, setCropPreviewLoading] = useState(false);
  const [cropLightboxOpen, setCropLightboxOpen] = useState(false);
  const [previewDrafts, setPreviewDrafts] = useState<PasHatchJobSnapshot['previewDrafts']>([]);
  const [expandedSequenceIds, setExpandedSequenceIds] = useState<Record<string, boolean>>({});
  const [drsLibraryScope, setDrsLibraryScope] = useState<{
    stepGranularity: 'major' | 'minor';
    hiddenLeafIds: string[];
  } | null>(null);
  const itemsRef = useRef<PasPracticeLibraryItem[]>([]);
  const openedAtRef = useRef(0);

  const isEnglish = useMemo(() => isEnglishPasSubject(subject), [subject]);
  const hatchPlaceholder = useMemo(() => resolvePasHatchPlaceholder(subject), [subject]);
  const hatchDirty = hatchPrompt.trim() !== savedHatchPrompt.trim();
  const hasPreview = previewDrafts.length > 0;

  const applyJobSnapshot = useCallback((snap: PasHatchJobSnapshot) => {
    const refreshed = refreshPasPracticeLibrary(snap.items);
    itemsRef.current = refreshed;
    setItems(refreshed);
    setPreviewDrafts(snap.previewDrafts || []);
    setLoading(snap.running);
    setStopping(snap.stopping);
    setProgress(snap.progress);
    setError(snap.error);
    setOfferProRetry(snap.offerProRetry);
    setUsingProModel(snap.usingProModel);
    if (snap.modelFeedback) {
      setModelFeedback(snap.modelFeedback);
    }
    if (snap.hatchPrompt) {
      setSavedHatchPrompt(snap.hatchPrompt);
      if (snap.running) {
        setHatchPrompt(snap.hatchPrompt);
      }
    }
  }, []);

  const commitItems = useCallback(async (next: PasPracticeLibraryItem[]) => {
    const refreshed = refreshPasPracticeLibrary(next);
    const targetSequenceId = regionId ?? PAS_ANCHOR_PRACTICE_SEQUENCE_ID;
    if (!onPracticeLibraryChange) return;

    if (resolveSubjectCode(subject) === 'math') {
      itemsRef.current = refreshed;
      await onPracticeLibraryChange(targetSequenceId, refreshed);
      return;
    }

    itemsRef.current = refreshed;
    setItems(refreshed);
    await onPracticeLibraryChange(targetSequenceId, refreshed);
    pasHatchSessionService.syncItems(assetId, targetSequenceId, refreshed, {
      hatchPrompt: savedHatchPrompt,
      modelFeedback,
    });
  }, [assetId, modelFeedback, onPracticeLibraryChange, regionId, savedHatchPrompt, subject]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    if (open) {
      openedAtRef.current = performance.now();
    }
  }, [open]);

  const handleBackdropClose = useCallback(() => {
    if (performance.now() - openedAtRef.current < 350) return;
    onClose();
  }, [onClose]);

  const activeCount = useMemo(() => countActivePasPracticeItems(items), [items]);
  const negativeCount = useMemo(
    () => items.filter((item) => item.negativeExample).length,
    [items],
  );
  const studyFullCount = useMemo(
    () => items.filter((item) => item.status === 'expired' && !item.negativeExample).length,
    [items],
  );
  const deficit = Math.max(0, targetCount - activeCount);
  const hatchComplexity = useMemo(
    () => assessPasHatchComplexity(hatchPrompt),
    [hatchPrompt],
  );

  const saveHatchPrompt = useCallback(() => {
    if (!regionId) return;
    const trimmed = hatchPrompt.trim();
    if (!trimmed) {
      setError('请填写孵化词');
      return;
    }
    if (trimmed === savedHatchPrompt.trim()) return;
    onHatchPromptChange?.(regionId, trimmed);
    setHatchPrompt(trimmed);
    setSavedHatchPrompt(trimmed);
    setError(null);
    onToast?.('孵化词已更新');
  }, [hatchPrompt, onHatchPromptChange, onToast, regionId, savedHatchPrompt]);

  const stopGenerate = useCallback(() => {
    if (!regionId || !loading) return;
    pasHatchSessionService.stop(assetId, regionId);
  }, [assetId, loading, regionId]);

  const discardEnglishPreview = useCallback(() => {
    if (!regionId) return;
    pasHatchSessionService.clearPreviewDrafts(assetId, regionId);
    setPreviewDrafts([]);
    onToast?.('已丢弃预览包');
  }, [assetId, onToast, regionId]);

  const confirmEnglishPreview = useCallback(() => {
    if (!regionId) return;
    const n = pasHatchSessionService.confirmPreviewDrafts(assetId, regionId);
    if (n <= 0) return;
    const snap = pasHatchSessionService.getSnapshot(assetId, regionId);
    if (snap) applyJobSnapshot(snap);
    onToast?.(`已入库 ${n} 道变式`);
  }, [applyJobSnapshot, assetId, onToast, regionId]);

  const runEnglishBatchGenerate = useCallback((options?: {
    prompt?: string;
    useProModel?: boolean;
  }) => {
    if (!regionId) return;
    const trimmed = String(options?.prompt ?? hatchPrompt).trim();
    if (!trimmed) {
      setError('请填写孵化词');
      return;
    }
    if (pasHatchSessionService.isRunning(assetId, regionId)) {
      const snap = pasHatchSessionService.getSnapshot(assetId, regionId);
      if (snap) applyJobSnapshot(snap);
      return;
    }
    if (hasPreview) return;

    onHatchPromptChange?.(regionId, trimmed);
    setHatchPrompt(trimmed);
    setSavedHatchPrompt(trimmed);
    setError(null);
    setOfferProRetry(false);

    pasHatchSessionService.start({
      assetId,
      regionId,
      sourceKpId,
      subject,
      hatchPrompt: trimmed,
      regionContent: localRegionContent || regionContent,
      regionFeedback: modelFeedback || regionFeedback,
      cropRectNormalized,
      initialItems: itemsRef.current,
      mode: 'english_batch',
      useProModel: options?.useProModel === true,
      onToast,
    });
  }, [
    applyJobSnapshot,
    assetId,
    cropRectNormalized,
    hasPreview,
    hatchPrompt,
    localRegionContent,
    modelFeedback,
    onHatchPromptChange,
    onToast,
    regionContent,
    regionFeedback,
    regionId,
    sourceKpId,
    subject,
  ]);

  /** Append-only generate via session service — survives drawer close. */
  const runSequentialGenerate = useCallback((options?: {
    count?: number;
    prompt?: string;
    useProModel?: boolean;
  }) => {
    if (!regionId) return;
    if (isEnglish) {
      runEnglishBatchGenerate({
        prompt: options?.prompt,
        useProModel: options?.useProModel,
      });
      return;
    }
    const trimmed = String(options?.prompt ?? hatchPrompt).trim();
    if (!trimmed) {
      setError('请填写孵化词');
      return;
    }
    if (pasHatchSessionService.isRunning(assetId, regionId)) {
      const snap = pasHatchSessionService.getSnapshot(assetId, regionId);
      if (snap) applyJobSnapshot(snap);
      return;
    }

    onHatchPromptChange?.(regionId, trimmed);
    setHatchPrompt(trimmed);
    setSavedHatchPrompt(trimmed);
    setError(null);
    setOfferProRetry(false);

    const total = Math.max(1, Math.min(9, options?.count ?? Math.max(1, deficit || targetCount)));
    pasHatchSessionService.start({
      assetId,
      regionId,
      sourceKpId,
      subject,
      hatchPrompt: trimmed,
      regionContent: localRegionContent || regionContent,
      regionFeedback: modelFeedback || regionFeedback,
      cropRectNormalized,
      initialItems: itemsRef.current,
      count: total,
      mode: 'sequential',
      useProModel: options?.useProModel === true,
      onToast,
      onRegionVision: (vision) => {
        setLocalRegionContent(vision.regionContent);
        if (vision.regionFeedback) {
          setModelFeedback(vision.regionFeedback);
        }
        onRegionVisionChange?.(regionId, vision);
      },
    });
  }, [
    applyJobSnapshot,
    assetId,
    cropRectNormalized,
    deficit,
    hatchPrompt,
    isEnglish,
    localRegionContent,
    modelFeedback,
    subject,
    onHatchPromptChange,
    onRegionVisionChange,
    onToast,
    regionContent,
    regionFeedback,
    regionId,
    runEnglishBatchGenerate,
    sourceKpId,
    targetCount,
  ]);

  /** Study count is incremented by Stream Provider when PKP is scheduled/reviewed — not here. */

  const savedLibraryItems = useMemo(
    () => refreshPasPracticeLibrary(initialPracticeItems || []),
    [initialPracticeItems],
  );

  const resolveLibraryMutationBase = useCallback((): PasPracticeLibraryItem[] => (
    resolveSubjectCode(subject) === 'math' ? savedLibraryItems : itemsRef.current
  ), [savedLibraryItems, subject]);

  const removeItem = useCallback((itemId: string) => {
    void commitItems(resolveLibraryMutationBase().filter((item) => item.id !== itemId));
  }, [commitItems, resolveLibraryMutationBase]);

  const toggleNegativeExample = useCallback((itemId: string) => {
    void commitItems(resolveLibraryMutationBase().map((item) => (
      item.id === itemId ? togglePasPracticeNegativeExample(item) : item
    )));
  }, [commitItems, resolveLibraryMutationBase]);

  const promoteToPracticeLibrary = useCallback(async (nextItems: PasPracticeLibraryItem[]) => {
    const targetSequenceId = regionId ?? PAS_ANCHOR_PRACTICE_SEQUENCE_ID;
    if (!onPracticeLibraryChange) {
      onToast?.('练习库尚未就绪，请稍后再试');
      return false;
    }
    const merged = mergePasPracticeLibraryItems(savedLibraryItems, nextItems);
    if (merged.length === savedLibraryItems.length) {
      onToast?.('这些题已在练习库');
      return false;
    }
    try {
      await onPracticeLibraryChange(targetSequenceId, merged);
      setExpandedSequenceIds((prev) => ({ ...prev, [targetSequenceId]: true }));
      return true;
    } catch {
      onToast?.('加入练习库失败，请重试');
      return false;
    }
  }, [
    onPracticeLibraryChange,
    onToast,
    regionId,
    savedLibraryItems,
  ]);

  const persistSequenceItems = useCallback((seqRegionId: string, next: PasPracticeLibraryItem[]) => {
    void onPracticeLibraryChange?.(seqRegionId, refreshPasPracticeLibrary(next));
  }, [onPracticeLibraryChange]);

  useEffect(() => {
    if (regionId) {
      setExpandedSequenceIds((prev) => (
        prev[regionId] ? prev : { ...prev, [regionId]: true }
      ));
      return;
    }
    if (resolveSubjectCode(subject) !== 'math') return;
    setExpandedSequenceIds((prev) => (
      prev[PAS_ANCHOR_PRACTICE_SEQUENCE_ID]
        ? prev
        : { ...prev, [PAS_ANCHOR_PRACTICE_SEQUENCE_ID]: true }
    ));
  }, [regionId, subject]);

  useEffect(() => {
    if (!open || resolveSubjectCode(subject) !== 'math') return;
    const hydrated = refreshPasPracticeLibrary(initialPracticeItems || []);
    itemsRef.current = hydrated;
  }, [initialPracticeItems, open, subject]);

  useEffect(() => {
    if (!open || !regionId) {
      return undefined;
    }

    setHatchPrompt(initialHatchPrompt);
    setSavedHatchPrompt(initialHatchPrompt);
    setModelFeedback(regionFeedback?.trim() || null);
    setLocalRegionContent(regionContent?.trim() || '');
    setVisionLoading(false);
    setCropPreviewUrl(null);
    setCropLightboxOpen(false);

    const snap = pasHatchSessionService.getSnapshot(assetId, regionId);
    if (snap && (snap.running || snap.items.length > 0 || snap.previewDrafts.length > 0)) {
      applyJobSnapshot(snap);
      if (!snap.hatchPrompt && initialHatchPrompt) {
        setHatchPrompt(initialHatchPrompt);
        setSavedHatchPrompt(initialHatchPrompt);
      }
    } else {
      const hydrated = refreshPasPracticeLibrary(initialPracticeItems || []);
      itemsRef.current = hydrated;
      setItems(hydrated);
      setPreviewDrafts([]);
      setError(null);
      setOfferProRetry(false);
      setProgress(null);
      setLoading(false);
      setStopping(false);
      setUsingProModel(false);
    }

    // Closing the drawer must NOT abort — hatch continues in session service.
    return pasHatchSessionService.subscribe(assetId, regionId, applyJobSnapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open/region gated hydrate
  }, [open, regionId, assetId]);

  useEffect(() => {
    if (!open || !regionId || !cropRectNormalized) {
      return undefined;
    }
    let cancelled = false;
    setCropPreviewLoading(true);
    void requestPasCropPreview({
      assetId,
      regionId,
      cropRectNormalized,
    }).then((result) => {
      if (cancelled) return;
      setCropPreviewUrl(result.cropImageUrl);
      setCropPreviewLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setCropPreviewUrl(null);
      setCropPreviewLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, regionId, assetId, cropRectNormalized]);

  const runRegionVision = useCallback(async () => {
    if (!regionId || !cropRectNormalized || visionLoading || loading) return;
    setVisionLoading(true);
    setError(null);
    try {
      const result = await requestPasRegionVision({
        assetId,
        regionId,
        sourceKpId,
        subject,
        hatchPrompt: hatchPrompt.trim() || undefined,
        cropRectNormalized,
      });
      if (result.regionFeedback) {
        setModelFeedback(result.regionFeedback);
        onToast?.(result.regionFeedback);
      }
      if (result.success && result.regionContent) {
        setLocalRegionContent(result.regionContent);
        if (result.cropImageUrl) {
          setCropPreviewUrl(result.cropImageUrl);
        }
        onRegionVisionChange?.(regionId, {
          regionContent: result.regionContent,
          regionFeedback: result.regionFeedback || '',
        });
        onToast?.('识图完成');
      } else {
        setError(result.regionFeedback || result.message || '识图失败，请重试');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '识图失败');
    } finally {
      setVisionLoading(false);
    }
  }, [
    assetId,
    cropRectNormalized,
    hatchPrompt,
    loading,
    onRegionVisionChange,
    onToast,
    regionId,
    sourceKpId,
    subject,
    visionLoading,
  ]);

  if (!open && presentation === 'drawer') {
    return null;
  }

  const showPendingSlot = loading && progress != null;
  const isMathDrs = resolveSubjectCode(subject) === 'math';
  const isPage = presentation === 'page';
  const sequences = regionSequences || [];
  const libraryItemsForUi = isMathDrs ? savedLibraryItems : items;
  const useGroupedLibrary = isMathDrs && sequences.length > 0;
  const libraryDisplayItems = useMemo(() => {
    if (!isMathDrs || useGroupedLibrary || !drsLibraryScope) return libraryItemsForUi;
    if (drsLibraryScope.stepGranularity === 'minor') return libraryItemsForUi;
    const hidden = new Set(drsLibraryScope.hiddenLeafIds);
    return libraryItemsForUi.filter((item) => !hidden.has(item.id));
  }, [drsLibraryScope, isMathDrs, libraryItemsForUi, useGroupedLibrary]);
  const libraryHiddenMinorCount = (
    isMathDrs
    && !useGroupedLibrary
    && drsLibraryScope?.stepGranularity === 'major'
  )
    ? Math.max(0, libraryItemsForUi.length - libraryDisplayItems.length)
    : 0;
  const libraryStatsItems = useGroupedLibrary
    ? sequences.flatMap((seq) => refreshPasPracticeLibrary(seq.practiceItems))
    : libraryDisplayItems;
  const libraryActiveCount = countActivePasPracticeItems(libraryStatsItems);
  const libraryNegativeCount = libraryStatsItems.filter((item) => item.negativeExample).length;
  const libraryStudyFullCount = libraryStatsItems.filter(
    (item) => item.status === 'expired' && !item.negativeExample,
  ).length;

  const renderLibraryItem = (
    item: PasPracticeLibraryItem,
    index: number,
    options: {
      onToggleNegative: () => void;
      onRemove: () => void;
    },
  ) => {
    const expired = item.status === 'expired';
    const isNegative = item.negativeExample === true;
    const proximity = typeof item.proximityLevel === 'number' ? item.proximityLevel : null;
    return (
      <li
        key={item.id}
        className={
          'rounded-xl border px-3 py-2.5 '
          + (isNegative
            ? 'border-danger/50 bg-danger/10 ring-1 ring-danger/30'
            : expired
              ? 'border-border/70 bg-canvas/40 opacity-75'
              : 'border-border bg-canvas/60')
        }
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <div className={`text-[11px] font-bold ${isNegative ? 'text-danger' : 'text-primary-strong'}`}>
                变式 {index + 1}
              </div>
              {isNegative ? (
                <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-danger/15 text-danger">
                  反例
                </span>
              ) : (
                <span
                  className={
                    'rounded px-1.5 py-0.5 text-[10px] font-semibold '
                    + (expired
                      ? 'bg-muted/20 text-muted'
                      : 'bg-primary/10 text-primary-strong')
                  }
                >
                  {expired ? '已学满' : '有效'}
                </span>
              )}
              <span className={`text-[10px] ${isNegative ? 'text-danger/80' : 'text-muted'}`}>
                已学 {item.studyCount}/{item.maxStudyCount} 次
              </span>
              {proximity != null ? <LibraryDifficultyBadge level={proximity} /> : null}
            </div>
            <p className={
              'mt-1 text-sm font-medium whitespace-pre-wrap '
              + (isNegative ? 'text-danger' : expired ? 'text-muted' : 'text-ink')
            }
            >
              {item.question}
            </p>
            <p className={`mt-1.5 text-xs ${isNegative ? 'text-danger/80' : 'text-muted'}`}>
              <span className={`font-semibold ${isNegative ? 'text-danger' : 'text-ink/70'}`}>答：</span>
              {item.answer}
            </p>
            {isNegative ? (
              <p className="mt-1 text-[10px] font-medium text-danger">
                已标为反例，后续生成会避开同类变式
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 flex-row items-center gap-0.5">
            <button
              type="button"
              disabled={loading}
              onClick={options.onToggleNegative}
              className={
                'inline-flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-50 '
                + (isNegative
                  ? 'bg-danger/15 text-danger hover:bg-danger/25'
                  : 'text-muted hover:bg-danger/10 hover:text-danger')
              }
              aria-label={isNegative ? `取消反例 变式 ${index + 1}` : `标为反例 变式 ${index + 1}`}
              aria-pressed={isNegative}
              title={isNegative ? '取消反例' : '标为反例（生成时避开）'}
            >
              <Ban size={14} />
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={options.onRemove}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-danger/10 hover:text-danger disabled:opacity-50"
              aria-label={`删除变式 ${index + 1}`}
              title="删除"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </li>
    );
  };

  if (!regionId && !isMathDrs) {
    return null;
  }

  const panelBody = (
    <>
        {isMathDrs ? (
          embedded ? (
            <header className="flex shrink-0 items-center border-b border-border px-2 py-1.5">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold text-ink transition hover:bg-canvas"
              >
                <ChevronLeft size={16} strokeWidth={2.2} />
                返回答题纸
              </button>
            </header>
          ) : null
        ) : (
        <header className="flex w-full items-start gap-2 border-b border-border px-4 py-3">
          <LandPlot size={18} className="mt-0.5 shrink-0 text-primary-strong" strokeWidth={2.2} />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-extrabold text-ink">刻意练习孵化</h2>
            <p className="mt-0.5 text-xs text-muted">
              {isPage
                ? '框选识图 + 孵化词共同出题。返回答题纸后生成仍会继续，可用「停止」中断。'
                : '框选识图 + 孵化词共同出题。关闭抽屉后生成仍会继续，可用「停止」中断。'}
            </p>
          </div>
          {!isPage ? (
            <button
              type="button"
              className="rounded-md p-1.5 text-muted hover:bg-canvas hover:text-ink"
              aria-label="关闭"
              onClick={onClose}
            >
              <X size={18} />
            </button>
          ) : null}
        </header>
        )}

        <div className="min-h-0 w-full flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {!isMathDrs ? (
          <div className="space-y-1.5">
            <span className="text-xs font-semibold text-ink">框选截图</span>
            <button
              type="button"
              disabled={!cropPreviewUrl || cropPreviewLoading}
              onClick={() => {
                if (cropPreviewUrl) setCropLightboxOpen(true);
              }}
              className={
                'relative flex h-28 w-full items-center justify-center overflow-hidden rounded-xl border border-border bg-canvas disabled:opacity-70 '
                + (!cropPreviewUrl && !cropPreviewLoading ? 'border-dashed' : '')
              }
              aria-label="查看框选截图"
            >
              {cropPreviewLoading ? (
                <Loader2 size={18} className="animate-spin text-muted" />
              ) : cropPreviewUrl ? (
                <>
                  <img
                    src={cropPreviewUrl}
                    alt="框选截图"
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute bottom-1.5 right-1.5 rounded-md bg-black/50 px-2 py-0.5 text-[10px] font-semibold text-white">
                    点击放大
                  </span>
                </>
              ) : (
                <span className="text-xs text-muted">暂无截图预览</span>
              )}
            </button>
          </div>
          ) : null}

          {resolveSubjectCode(subject) === 'math' ? (
            <PasDrsPanel
              assetId={assetId}
              regionId={regionId}
              sourceKpId={sourceKpId}
              subject={subject}
              autoEnumerate={autoEnumerateDrs}
              libraryItems={savedLibraryItems}
              onPromoteLeaves={promoteToPracticeLibrary}
              onRemoveLibraryItems={(ids) => {
                const idSet = new Set(ids.map((id) => String(id || '').trim()).filter(Boolean));
                if (idSet.size === 0) return;
                void commitItems(resolveLibraryMutationBase().filter((item) => !idSet.has(item.id)));
              }}
              onLibraryDisplayScope={setDrsLibraryScope}
              onToast={onToast}
            />
          ) : null}

          {!isMathDrs ? (
          <>
          <div className="space-y-2">
            {(modelFeedback || localRegionContent) ? (
              <>
                {modelFeedback ? (
                  <div className="rounded-xl border border-amber-300/80 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-950">
                    <p className="font-semibold text-amber-900">识图反馈（qwen，与出题分离）</p>
                    <p className="mt-1">{modelFeedback}</p>
                    <p className="mt-1.5 text-[11px] text-amber-800/80">
                      仅供参考；「生成一道题」只走 DeepSeek，不会重新识图。缺口用下方孵化词补足。
                    </p>
                  </div>
                ) : null}
                {localRegionContent ? (
                  <div className="rounded-xl border border-border bg-canvas px-3 py-2 text-[11px] leading-relaxed text-muted">
                    <p className="font-semibold text-ink">框选区识别</p>
                    <p className="mt-1 line-clamp-8 whitespace-pre-wrap">{localRegionContent}</p>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="rounded-xl border border-amber-300/80 bg-amber-50 px-3 py-2 text-[12px] leading-relaxed text-amber-950">
                <p className="font-semibold text-amber-900">尚未识图</p>
                <p className="mt-1">框选区还没有识别内容。请先点「重新识图」，再结合孵化词生成变式。</p>
              </div>
            )}
            <button
              type="button"
              disabled={loading || visionLoading || !cropRectNormalized}
              onClick={() => void runRegionVision()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-3 py-2.5 text-sm font-semibold text-ink hover:bg-canvas disabled:opacity-50"
            >
              {visionLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {visionLoading ? '识图中…' : (localRegionContent ? '重新识图' : '开始识图')}
            </button>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-ink">卡点说明（可选）</span>
              {hatchDirty ? (
                <span className="text-[10px] font-medium text-muted">未保存</span>
              ) : null}
            </div>
            <textarea
              value={hatchPrompt}
              onChange={(event) => setHatchPrompt(event.target.value)}
              rows={5}
              disabled={loading || visionLoading}
              className="w-full resize-y rounded-xl border border-border bg-[#F8FAFD] px-3 py-2 text-sm text-ink outline-none focus:border-primary disabled:opacity-70"
              placeholder={hatchPlaceholder}
            />
            {hatchComplexity.warning ? (
              <div className={
                'rounded-xl border px-3 py-2 text-[11px] leading-relaxed '
                + (hatchComplexity.level === 'high'
                  ? 'border-amber-300/80 bg-amber-50 text-amber-900'
                  : 'border-border bg-canvas text-muted')
              }
              >
                <p className="font-semibold text-amber-800">复杂度提示（不阻断生成）</p>
                <p className="mt-1">{hatchComplexity.warning}</p>
                {hatchComplexity.reasons.length > 1 ? (
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
                    {hatchComplexity.reasons.slice(1).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <div className="flex justify-end">
              <button
                type="button"
                disabled={loading || !hatchDirty || !hatchPrompt.trim()}
                onClick={saveHatchPrompt}
                className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/90 disabled:opacity-50"
              >
                保存孵化词
              </button>
            </div>
          </div>

          {!isEnglish ? (
            <div className="space-y-1.5">
              <span className="text-xs font-semibold text-ink">有效题目标数</span>
              <div className="flex flex-wrap gap-2">
                {TARGET_COUNT_OPTIONS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    disabled={loading}
                    onClick={() => setTargetCount(count)}
                    className={
                      'inline-flex h-8 min-w-8 items-center justify-center rounded-lg border px-2.5 text-sm font-medium transition disabled:opacity-60 '
                      + (targetCount === count
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-surface text-ink hover:bg-canvas')
                    }
                  >
                    {count}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted">
                当前有效 {activeCount} / 目标 {targetCount}
                {deficit > 0 ? `，还需补 ${deficit} 道` : '，已够用'}
                。学满题保留在列表中，可继续生成新题。
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <span className="text-xs font-semibold text-ink">变式生成</span>
              <p className="text-[11px] text-muted">
                按识图与关联上下文自动决定题量（如词表则一词一题）。发起后在后台生成，可关闭抽屉；完成后回来预览确认再入库。
                当前库内有效 {activeCount} 道。
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            {loading ? (
              <button
                type="button"
                onClick={stopGenerate}
                disabled={stopping}
                className={
                  'inline-flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold '
                  + (stopping
                    ? 'border-border bg-canvas text-muted'
                    : 'border-border bg-surface text-ink')
                }
              >
                {stopping ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Square size={14} className="fill-current" />
                )}
                {stopping
                  ? '停止中…'
                  : isEnglish
                    ? '停止生成'
                    : `停止生成${progress ? `（${progress.current}/${progress.total}）` : ''}`}
              </button>
            ) : isEnglish ? (
              <>
                <button
                  type="button"
                  disabled={hasPreview}
                  onClick={() => runEnglishBatchGenerate()}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <RefreshCw size={16} />
                  {hasPreview ? '请先确认或丢弃预览包' : '后台生成变式包'}
                </button>
                {hatchComplexity.suggestProModel && !loading ? (
                  <button
                    type="button"
                    disabled={hasPreview}
                    onClick={() => runEnglishBatchGenerate({ useProModel: true })}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-400/70 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100/80 disabled:opacity-50"
                  >
                    用 Pro 后台生成变式包（更稳，更慢）
                  </button>
                ) : null}
              </>
            ) : (
              <>
                <button
                  type="button"
                  disabled={deficit <= 0}
                  onClick={() => void runSequentialGenerate({ count: Math.max(1, deficit) })}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <RefreshCw size={16} />
                  {deficit > 0
                    ? `补足 ${deficit} 道有效题`
                    : `有效题已达 ${targetCount}`}
                </button>
                {hatchComplexity.suggestProModel && !loading ? (
                  <button
                    type="button"
                    disabled={deficit <= 0 && activeCount >= targetCount}
                    onClick={() => void runSequentialGenerate({
                      count: Math.max(1, deficit || 1),
                      useProModel: true,
                    })}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-400/70 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100/80 disabled:opacity-50"
                  >
                    用 Pro 模型补足（更稳，更慢）
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void runSequentialGenerate({ count: 1 })}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-ink hover:bg-canvas"
                >
                  <Plus size={16} />
                  生成一道题（追加）
                </button>
              </>
            )}
          </div>

          {loading && progress ? (
            <p className="text-sm text-muted">
              {stopping
                ? (isEnglish
                  ? '正在停止批量生成…'
                  : `正在停止，等待第 ${progress.current}/${progress.total} 次请求结束…`)
                : isEnglish
                  ? (usingProModel
                    ? '后台生成变式包中（Pro）…可关闭本抽屉，完成后会提示预览'
                    : '后台生成变式包中…可关闭本抽屉，完成后会提示预览')
                  : usingProModel
                    ? `逐题生成中：第 ${progress.current}/${progress.total} 次请求（Pro，已成功 ${progress.produced ?? items.length} 道${progress.failed ? `，跳过 ${progress.failed}` : ''}）…`
                    : `逐题生成中：第 ${progress.current}/${progress.total} 次请求（已成功 ${progress.produced ?? items.length} 道${progress.failed ? `，跳过 ${progress.failed}` : ''}）…`}
            </p>
          ) : null}

          {hasPreview ? (
            <div className="space-y-2 rounded-xl border border-border bg-canvas/50 p-3">
              <h3 className="text-xs font-semibold text-ink">
                预览包（{previewDrafts.length} 道，未入库）
              </h3>
              <p className="text-[11px] text-muted">确认后写入题目库；丢弃则不保存。</p>
              <ul className="space-y-2">
                {previewDrafts.map((draft, index) => (
                  <li key={draft.id || `preview-${index}`} className="rounded-lg border border-border bg-surface px-3 py-2">
                    <p className="text-[11px] font-semibold text-muted">预览 {index + 1}</p>
                    <p className="mt-1 text-sm text-ink">{draft.question}</p>
                    {draft.answer ? (
                      <p className="mt-1 text-xs text-muted">答：{draft.answer}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={confirmEnglishPreview}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white"
              >
                确认入库（{previewDrafts.length} 道）
              </button>
              <button
                type="button"
                onClick={discardEnglishPreview}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-ink hover:bg-canvas"
              >
                丢弃预览包
              </button>
            </div>
          ) : null}

          {error ? (
            <div className="space-y-2">
              <p className={
                error.includes('本批成功')
                  || error.includes('已停止：本批完成')
                  || isHeuristicPrinciplesFailMessage(error)
                  || error.includes('偏复杂')
                  || error.includes('软警告')
                  ? 'text-sm text-amber-700'
                  : 'text-sm text-danger'
              }
              >
                {isHeuristicPrinciplesFailMessage(error)
                  ? `软警告（不阻断）：${error}。应继续交给模型出题；若仍秒失败，请重新部署 pas-math-pkp / AppFunctor。`
                  : error}
              </p>
              {offerProRetry && !loading ? (
                <div className="space-y-1.5 rounded-xl border border-border bg-canvas/70 px-3 py-2.5">
                  <p className="text-xs text-muted">
                    场景较复杂时，可改用 Pro 模型再试（耗时更长）。
                  </p>
                  <button
                    type="button"
                    onClick={() => void (isEnglish
                      ? runEnglishBatchGenerate({ useProModel: true })
                      : runSequentialGenerate({
                        count: Math.max(1, deficit || 1),
                        useProModel: true,
                      }))}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary-strong hover:bg-primary/15"
                  >
                    使用 Pro 模型再试
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          </>
          ) : null}

          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-ink">
              题目库
              {libraryStatsItems.length > 0
                ? `（共 ${libraryStatsItems.length}，有效 ${libraryActiveCount}，学满 ${libraryStudyFullCount}${libraryNegativeCount > 0 ? `，反例 ${libraryNegativeCount}` : ''}）`
                : ''}
            </h3>
            {libraryHiddenMinorCount > 0 ? (
              <p className="text-[11px] text-muted">
                大台阶视图下另有 {libraryHiddenMinorCount} 道小台阶题已入库（折叠在上方大台阶内）。
                切换到「小台阶」可查看与管理；确认加入练习库仅收录当前视图的大台阶代表题。
              </p>
            ) : null}
            {!loading && libraryStatsItems.length === 0 && !error && !hasPreview ? (
              <p className="text-xs text-muted">
                {isEnglish
                  ? '尚无题目。点「后台生成变式包」，完成后预览确认再入库。'
                  : isMathDrs
                    ? useGroupedLibrary
                      ? '尚无题目。展开上方起手式或框选局部阶梯，点「确认加入练习库」收录到对应组。'
                      : '尚无题目。展开路径生成台阶练习后，点「确认加入练习库」收录。'
                    : '尚无题目。有效题为 0 时点击补足生成。'}
              </p>
            ) : null}

            {useGroupedLibrary ? (
              <div className="space-y-3">
                {sequences.map((seq) => {
                  const activeSequenceId = regionId ?? PAS_ANCHOR_PRACTICE_SEQUENCE_ID;
                  const isActive = seq.id === activeSequenceId;
                  const expanded = expandedSequenceIds[seq.id] === true;
                  const ordered = sortPracticeItemsByProximity(seq.practiceItems);
                  const groupNo = typeof seq.groupIndex === 'number' ? seq.groupIndex : 0;
                  const groupTitle = isPasAnchorPracticeSequenceId(seq.id)
                    ? '起手式展开'
                    : (seq.label || `框选 ${groupNo}`);
                  return (
                    <div
                      key={seq.id}
                      className={
                        'overflow-hidden rounded-xl border '
                        + (isActive
                          ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20'
                          : 'border-border bg-canvas/40')
                      }
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (isActive) {
                            setExpandedSequenceIds((prev) => ({
                              ...prev,
                              [seq.id]: !expanded,
                            }));
                            return;
                          }
                          onSelectRegionSequence?.(seq.id);
                          setExpandedSequenceIds((prev) => ({
                            ...prev,
                            [seq.id]: true,
                          }));
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition hover:bg-canvas/80"
                      >
                        {expanded
                          ? <ChevronDown size={14} className="shrink-0 text-muted" />
                          : <ChevronRight size={14} className="shrink-0 text-muted" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            <span className="text-[11px] font-bold text-ink">
                              第 {groupNo} 组
                            </span>
                            <span className="text-[10px] text-muted">
                              {groupTitle}
                            </span>
                            <span className="text-[10px] text-muted">
                              · {ordered.length} 道
                            </span>
                            {isActive ? (
                              <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold bg-primary/15 text-primary-strong">
                                当前
                              </span>
                            ) : null}
                          </div>
                          {seq.label ? (
                            <p className="mt-0.5 truncate text-[11px] text-muted">{seq.label}</p>
                          ) : null}
                        </div>
                      </button>
                      {expanded ? (
                        <ul className="space-y-2 border-t border-border/70 px-2 py-2">
                          {ordered.length === 0 ? (
                            <li className="px-2 py-2 text-[11px] text-muted">
                              {isActive
                                ? (isPasAnchorPracticeSequenceId(seq.id)
                                  ? '展开上方五种起手式并点「确认加入练习库」，题目会出现在第 0 组。'
                                  : '展开上方阶梯并点「确认加入练习库」，题目会出现在本组。')
                                : '点此切换到该组后展开阶梯练习。'}
                            </li>
                          ) : (
                            ordered.map((item, index) => renderLibraryItem(item, index, {
                              onToggleNegative: () => {
                                persistSequenceItems(
                                  seq.id,
                                  seq.practiceItems.map((row) => (
                                    row.id === item.id
                                      ? togglePasPracticeNegativeExample(row)
                                      : row
                                  )),
                                );
                              },
                              onRemove: () => {
                                persistSequenceItems(
                                  seq.id,
                                  seq.practiceItems.filter((row) => row.id !== item.id),
                                );
                              },
                            }))
                          )}
                          {isActive && showPendingSlot ? (
                            <li
                              className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-3"
                              aria-busy="true"
                              aria-label={`正在生成下一道变式（请求 ${progress?.current}/${progress?.total}）`}
                            >
                              <div className="flex items-center gap-2 text-[11px] font-bold text-primary-strong">
                                <Loader2 size={14} className="animate-spin" />
                                下一道变式
                              </div>
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
            <ul className="space-y-3">
              {libraryDisplayItems.map((item, index) => renderLibraryItem(item, index, {
                onToggleNegative: () => toggleNegativeExample(item.id),
                onRemove: () => removeItem(item.id),
              }))}

              {showPendingSlot ? (
                <li
                  className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-3 py-3"
                  aria-busy="true"
                  aria-label={`正在生成下一道变式（请求 ${progress?.current}/${progress?.total}）`}
                >
                  <div className="flex items-center gap-2 text-[11px] font-bold text-primary-strong">
                    <Loader2 size={14} className="animate-spin" />
                    下一道变式
                    <span className="font-medium text-muted">
                      请求 {progress?.current}/{progress?.total}
                      {typeof progress?.produced === 'number' ? ` · 已成功 ${progress.produced}` : ''}
                      …
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-muted">
                    每成功一道会立刻加入题目库；失败会跳过并继续下一道，不会等整批结束才显示。
                  </p>
                  <div className="mt-2 h-3 w-[80%] animate-pulse rounded bg-primary/10" />
                  <div className="mt-1.5 h-3 w-[65%] animate-pulse rounded bg-primary/10" />
                </li>
              ) : null}
            </ul>
            )}
          </section>
        </div>

        <footer className="border-t border-border px-4 py-3 text-[11px] text-muted">
          学满仅标记失效；Ban 标反例（飘红）供后续生成避开；也可真删除。已学次数由复习 Stream 累加。
        </footer>

      {cropLightboxOpen && cropPreviewUrl ? (
        <div
          className="absolute inset-0 z-[60] flex flex-col bg-black/92"
          role="dialog"
          aria-modal="true"
          aria-label="框选截图预览"
        >
          <div className="flex shrink-0 items-center justify-between px-4 py-3">
            <p className="text-sm font-bold text-white">框选截图</p>
            <button
              type="button"
              className="rounded-md p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
              aria-label="关闭预览"
              onClick={() => setCropLightboxOpen(false)}
            >
              <X size={20} />
            </button>
          </div>
          <button
            type="button"
            className="relative min-h-0 flex-1 px-3 pb-6"
            onClick={() => setCropLightboxOpen(false)}
            aria-label="关闭预览"
          >
            <img
              src={cropPreviewUrl}
              alt="框选截图放大"
              className="absolute inset-3 h-[calc(100%-1.5rem)] w-[calc(100%-1.5rem)] object-contain"
              onClick={(event) => event.stopPropagation()}
            />
          </button>
        </div>
      ) : null}
    </>
  );

  if (isPage) {
    return (
      <div className={`relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-surface${embedded || isMathDrs ? '' : ' pl-12'}`}>
        {panelBody}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 z-40 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-ink/20"
        aria-label="关闭抽屉"
        onClick={handleBackdropClose}
      />
      <aside className="relative z-10 flex h-full w-[96vw] flex-col border-l border-border bg-surface shadow-card">
        {panelBody}
      </aside>
    </div>
  );
}
